import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { usePlatformStore, useVideoMeetingDetails } from "@platform/store";
import { VideoMeetingLoading } from "@platform/features/videoMeeting";
import { useFetchVideoMeetingDetails } from "@core/features/videoMeeting";
import { VideoMeetingEndQuizAction } from "@platform/features/videoMeeting/components/VideoMeetingEnd/VideoMeetingEndQuizAction";
import { ProgramDialogs } from "@platform/features/programDialogs";
import useMeetingRelatedUserPhaseAction from "@platform/features/video/hooks/useMeetingRelatedUserPhaseAction";

export function VideoEndedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { meetingId: meetingIdFromUrl } = useParams();
  const { meetingId, meetingDetails: storeMeetingDetails } =
    useVideoMeetingDetails();
  const user = usePlatformStore((s) => s.user);

  const resolvedMeetingId =
    meetingIdFromUrl || (meetingId != null ? String(meetingId) : null);

  // Fetch meeting details from the API — survives store reset and page reload
  const { data: fetchedDetails, isLoading: isFetchingDetails } =
    useFetchVideoMeetingDetails({
      meetingId: resolvedMeetingId,
      user,
      isEnabled: !!resolvedMeetingId && !storeMeetingDetails,
    });

  const meetingDetails = storeMeetingDetails ?? fetchedDetails ?? null;

  // Auto-refresh after 0.5 seconds to ensure device cleanup (only if not already refreshed)
  useEffect(() => {
    const hasRefreshed = searchParams.get("refreshed") === "true";

    if (!hasRefreshed) {
      const timer = setTimeout(() => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("refreshed", "true");
        window.location.replace(currentUrl.toString());
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!meetingIdFromUrl && !meetingId) {
      navigate("/dashboard");
    }
  }, [meetingIdFromUrl, meetingId, navigate]);

  const meetingSurveyAction =
    useMeetingRelatedUserPhaseAction(
      resolvedMeetingId,
      "meeting_survey",
      meetingDetails?.program ?? 0
    ) || null;

  if (!meetingDetails && (isFetchingDetails || !searchParams.get("refreshed"))) {
    return <VideoMeetingLoading />;
  }

  if (!meetingDetails) {
    return <VideoMeetingLoading />;
  }

  return (
    <>
      <div className="flex flex-col items-start justify-center flex-1 py-8 md:items-center">
        <OuterContentContainer innerClassName="flex flex-col-reverse gap-8 w-auto md:flex-row md:gap-0 md:items-stretch md:border md:border-brand-800 md:rounded-md md:p-0 md:shadow-lg">
          <div className="flex flex-col gap-8 w-full md:min-w-lg md:max-w-xl md:gap-8 md:px-12 md:py-8">
            <div className="flex flex-1 flex-col gap-4 items-center text-center">
              <Type variant="body2">{meetingDetails.title}</Type>
              <Type as="h1" variant="h3">
                The meeting has ended.
              </Type>
              <Type variant="body1">
                Thank you for attending.{" "}
                {meetingDetails.meeting_type !== "meeting_standalone" &&
                  "Please take a moment to fill out this 3-minute feedback survey."}
              </Type>
            </div>
            <div className="flex flex-col gap-4">
              {meetingDetails.meeting_type !== "meeting_standalone" &&
                (meetingSurveyAction ? (
                  <VideoMeetingEndQuizAction action={meetingSurveyAction} />
                ) : (
                  <Loader />
                ))}
              <Button as={Link} to="/dashboard" variant="secondary">
                Go to Dashboard
              </Button>
            </div>
          </div>
        </OuterContentContainer>
      </div>
      <ProgramDialogs />
    </>
  );
}

export default VideoEndedPage;
